import { createSelector } from '../selectorWrapper'
import { imagesSelector, liveStateSelector } from '../detailFragment'
import { pageSelectors, simpleSelectors } from '../basicState'
import {
  isRetailSelector,
  urlMakerSelector,
  MakeUrl,
  marketplaceIdSelector,
} from '../requestContext'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { isGen4, isTVSelector } from '../shared'
import { TitleIdToDetail } from '../../reducers/detail'
import { TitleIdToSelf } from '../../reducers/self'
import { multiplePlaybackComponentSelector, MultiplePlaybackData } from './actionBoxComponentSelectors'
import { SeasonInfo } from '../../reducers/seasons'
import { isIosMShopSelector, isMobileSelector } from '../pageContext'
import { watchPartyActionSelector } from '../actionFragment'
import { isJPMarketplace } from '../../utils/marketplace'

export const titleComponentSelector = createSelector(
  [urlMakerSelector, isRetailSelector, pageSelectors.headerDetail],
  (makeUrl: MakeUrl, isRetail: boolean, detailFragment?: WpsRuntime.atv.wps.DetailFragmentModelProps) => {
    if (detailFragment) {
      return {
        title: detailFragment.parentTitle || detailFragment.title,
        asin: detailFragment.asin,
        cGti: detailFragment.compactGti,
        makeUrl,
        isRetail
      }
    }
    return undefined
  }
)

export const metaDataSelector = createSelector(
  [isTVSelector, simpleSelectors.isStarlightEnhanced, pageSelectors.headerDetail],
  (
    isTV: boolean,
    isStarlightEnhanced: boolean,
    detailFragment?: WpsRuntime.atv.wps.DetailFragmentModelProps
  ) => {
    if (detailFragment) {
      return { ...detailFragment, isTV, isStarlightEnhanced }
    }
    return undefined
  }
)

export const heroshotShouldBeShownSelector = createSelector(
  [marketplaceIdSelector, imagesSelector],
  (
    marketplaceId: string,
    images?: WpsRuntime.atv.wps.ImagesProps
  ) => {
    return Boolean(!isJPMarketplace(marketplaceId) && images && images.heroshot)
  }
)

export const synopsisSelector = createSelector(
  [simpleSelectors.isStarlightEnhanced, pageSelectors.headerDetail],
  (isStarlightEnhanced: boolean, detailFragment?: WpsRuntime.atv.wps.DetailFragmentModelProps) =>
    (detailFragment && !isStarlightEnhanced && { synopsis: detailFragment.synopsis }) || undefined
)

export const liveStateDataSelector = createSelector(
  liveStateSelector,
  (liveState?: WpsRuntime.atv.wps.LiveStateProps) =>
    (liveState && {
      text: liveState.text,
      isLive: liveState.isLive
    } || undefined)
)

export const seasonsSelector = createSelector(
  [
    simpleSelectors.detail,
    simpleSelectors.self,
    pageSelectors.seasons,
    simpleSelectors.pageTitleId,
    isRetailSelector
  ],
  (
    details: TitleIdToDetail,
    self: TitleIdToSelf,
    seasons?: SeasonInfo[],
    pageTitleId?: WpsRuntime.atv.wps.TitleID,
    isRetail?: boolean
  ) => ({
    seasons: seasons
      ? seasons.map(season => ({
          titleId: season.seasonId,
          seasonLink: season.seasonLink,
          detail: details[season.seasonId],
          self: self[season.seasonId]
        }))
      : undefined,
    pageTitleId,
    isRetail
  })
)

// Returns true if the atf has minimum data needed to render, false otherwise
export const atfCriticalDataSelector = createSelector(
  [
    isGen4,
    pageSelectors.headerDetail,
    multiplePlaybackComponentSelector,
  ],
  (
    isGen4: boolean,
    detailFragment?: WpsRuntime.atv.wps.DetailFragmentModelProps,
    playbackActions?: MultiplePlaybackData,
  ) => {
    const hasTitle = Boolean(detailFragment && detailFragment.title)
    const hasPlayback = Boolean(playbackActions)

    return isGen4 ?
      hasTitle || hasPlayback :
      hasTitle
  }
)

export const renderOnlyGen4PlaybackInAtfSelector = createSelector(
  [
    isGen4,
    pageSelectors.headerDetail,
    multiplePlaybackComponentSelector,
  ],
  (
    isGen4: boolean,
    detailFragment?: WpsRuntime.atv.wps.DetailFragmentModelProps,
    playbackActions?: MultiplePlaybackData,
  ) => {
    const missingTitle = !Boolean(detailFragment && detailFragment.title)
    const hasPlayback = Boolean(playbackActions)
    return isGen4 && missingTitle && hasPlayback
  }
)

export const bottomBarSelector = createSelector(
  [
    isMobileSelector,
    isIosMShopSelector,
    isRetailSelector,
    simpleSelectors.bottomBar,
    watchPartyActionSelector
  ],
  (
    isMobile: boolean,
    isPurchaseRestricted: boolean,
    isRetail: boolean,
    bottomBar?: WpsRuntime.atv.wps.BottomBarModelProps,
    watchPartyAction?: WpsRuntime.atv.wps.WatchPartyAction
  ) => (
    bottomBar && {
      isMobile,
      isPurchaseRestricted,
      isRetail,
      bottomBar,
      watchPartyAction
    } || undefined
  )
)